import { useMsal } from '@azure/msal-react';
import { useEffect, useCallback, useState } from 'react';
import { loginRequest, graphConfig } from '../config/authConfig';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();

  // Estados para validación de acceso
  const [validandoAcceso, setValidandoAcceso] = useState(false);
  const [accesoAutorizado, setAccesoAutorizado] = useState(false);
  const [accesoYaIntentado, setAccesoYaIntentado] = useState(false); // NUEVO
  const [mensajeAcceso, setMensajeAcceso] = useState('');
  const [datosUsuarioAutorizado, setDatosUsuarioAutorizado] = useState(null);

  const verificarAccesoUsuario = useCallback(async (email) => {
    if (!email) {
      console.log('No hay email para verificar');
      return { autorizado: false, mensaje: 'Email no disponible' };
    }

    setValidandoAcceso(true);
    setMensajeAcceso('Verificando permisos de acceso...');

    try {
      const response = await fetch(`${API_BASE_URL}/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.autorizado) {
        console.log('✅ Acceso autorizado para:', data.usuario);
        setAccesoAutorizado(true);
        setDatosUsuarioAutorizado(data.usuario);
        setMensajeAcceso(data.mensaje);
        return { autorizado: true, usuario: data.usuario };
      } else {
        console.warn('⛔ Acceso denegado:', data.mensaje);
        setAccesoAutorizado(false);
        setDatosUsuarioAutorizado(null);
        setMensajeAcceso(data.mensaje || 'Acceso denegado');
        return { autorizado: false, mensaje: data.mensaje };
      }
    } catch (error) {
      console.error('❌ Error verificando acceso:', error);
      setAccesoAutorizado(false);
      setDatosUsuarioAutorizado(null);
      setMensajeAcceso('Error verificando permisos de acceso');
      return { autorizado: false, mensaje: 'Error verificando permisos' };
    } finally {
      setValidandoAcceso(false);
      setAccesoYaIntentado(true); // 🔄 Marcar que ya se intentó
    }
  }, []);

  const crearHeadersAutorizados = useCallback(() => {
    const account = accounts[0];
    if (!account) return {};
    return {
      'Content-Type': 'application/json',
      'x-user-email': account.username || '',
    };
  }, [accounts]);

  const login = async () => {
    try {
      console.log('🔐 Iniciando login...');
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Error durante login:', error);
    }
  };

  const logout = async () => {
    try {
      const account = accounts[0];
      if (account) {
        console.log('🔒 Cerrando sesión de:', account.name || account.username);
      }
      setAccesoAutorizado(false);
      setDatosUsuarioAutorizado(null);
      setMensajeAcceso('');
      setValidandoAcceso(false);
      setAccesoYaIntentado(false); // 🔁 Permitir nuevo intento después de logout
      await instance.logoutRedirect();
    } catch (error) {
      console.error('Error durante logout:', error);
    }
  };

  const getAccessToken = async () => {
    const account = accounts[0];
    if (account) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account,
        });
        return response.accessToken;
      } catch (error) {
        console.error('Error obteniendo token:', error);
        return null;
      }
    }
    return null;
  };

  const getUserInfo = useCallback(() => {
    const account = accounts[0];
    if (account) {
      return {
        ...account,
        displayName: account.name,
        email: account.username || '',
        userId: account.localAccountId || account.homeAccountId,
        tenant: account.tenantId,
        claims: account.idTokenClaims
      };
    }
    return null;
  }, [accounts]);

  const showUserInfo = useCallback(() => {
    const user = getUserInfo();
    console.log('👤 Usuario autenticado:', user);
  }, [getUserInfo]);

  // 🔁 VALIDACIÓN AUTOMÁTICA DE ACCESO (solo una vez)
  useEffect(() => {
    const validarAccesoAutomatico = async () => {
      if (
        accounts.length > 0 &&
        inProgress === 'none' &&
        !validandoAcceso &&
        !accesoAutorizado &&
        !accesoYaIntentado
      ) {
        const account = accounts[0];
        const email = account?.username?.toLowerCase().trim();

        if (email && email.includes('@')) {
          console.log('🚦 Verificando acceso automático para:', email);
          await verificarAccesoUsuario(email);
        } else {
          console.warn('❗ Email inválido para validar acceso:', email);
          setAccesoAutorizado(false);
          setMensajeAcceso('No se pudo obtener email válido para validar acceso');
          setAccesoYaIntentado(true);
        }
      }
    };

    validarAccesoAutomatico();
  }, [
    accounts,
    inProgress,
    validandoAcceso,
    accesoAutorizado,
    accesoYaIntentado,
    verificarAccesoUsuario,
  ]);

  // Exponer global para debug (opcional)
  useEffect(() => {
    if (accounts.length > 0 && accesoAutorizado) {
      window.showUserInfo = showUserInfo;
      window.currentUser = getUserInfo();
      window.userAccess = {
        autorizado: accesoAutorizado,
        datos: datosUsuarioAutorizado,
        mensaje: mensajeAcceso,
      };
      return () => {
        delete window.showUserInfo;
        delete window.currentUser;
        delete window.userAccess;
      };
    }
  }, [accounts.length, showUserInfo, getUserInfo, accesoAutorizado, datosUsuarioAutorizado, mensajeAcceso]);

  return {
    isAuthenticated: accounts.length > 0,
    user: getUserInfo(),
    login,
    logout,
    getAccessToken,
    showUserInfo,
    isLoading: inProgress === 'login' || inProgress === 'logout',
    rawAccounts: accounts,

    // Estado de acceso
    validandoAcceso,
    accesoAutorizado,
    mensajeAcceso,
    datosUsuarioAutorizado,

    // Utilidades
    verificarAccesoUsuario,
    crearHeadersAutorizados,

    // Estados combinados
    isAuthenticatedAndAuthorized: accounts.length > 0 && accesoAutorizado,
    isReady: accounts.length > 0 && !validandoAcceso,

    // Debug info
    debugInfo: {
      accountsCount: accounts.length,
      inProgress,
      hasAccounts: accounts.length > 0,
      validandoAcceso,
      accesoAutorizado,
      accesoYaIntentado,
      usuarioAutorizado: datosUsuarioAutorizado?.nombre || 'Sin datos'
    }
  };
};
